/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import styled from "styled-components";
import { theme } from "@nordcloud/gnui";
import { getTooltipSettings } from "../utils";

type Props = {
  planName: string;
  actionName: string;
  scheduleTimezone: string;
  startTime: string;
  endTime: string;
  resourceCount?: number;
  inputParameters?: Array<{ key: string; value: string }>;
  accentColor?: string;
  isPast?: boolean;
  eventStatus?: string;
  actionStatus?: string;
  onGoToPlan: () => void;
  onGoToEvent: () => void;
  onFilterPlan: () => void;
};

export function EventPopover({
  planName,
  actionName,
  scheduleTimezone,
  startTime,
  endTime,
  resourceCount,
  inputParameters,
  accentColor,
  isPast,
  eventStatus,
  actionStatus,
  onGoToPlan,
  onGoToEvent,
  onFilterPlan,
}: Props) {
  const settings = getTooltipSettings(actionName, inputParameters ?? []);

  return (
    <PopoverContainer
      $accentColor={accentColor}
      data-testid="timeline-event-popover"
    >
      <PopoverHeader>
        <ActionName>{actionName}</ActionName>
        {isPast && actionStatus && (
          <StatusBadge $status={actionStatus}>
            {getStatusLabel(actionStatus)}
          </StatusBadge>
        )}
      </PopoverHeader>

      <PopoverBody>
        <InfoRow>
          <InfoLabel>📋</InfoLabel>
          <InfoValue>{planName}</InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>🕐</InfoLabel>
          <InfoValue>
            {formatTime(startTime)} – {formatTime(endTime)} ({scheduleTimezone})
          </InfoValue>
        </InfoRow>

        {resourceCount != null && resourceCount > 0 && (
          <InfoRow>
            <InfoLabel>🖥️</InfoLabel>
            <InfoValue>
              {resourceCount} resource{resourceCount !== 1 ? "s" : ""}
            </InfoValue>
          </InfoRow>
        )}

        {isPast && eventStatus && (
          <InfoRow>
            <InfoLabel>📊</InfoLabel>
            <InfoValue>Event: {getStatusLabel(eventStatus)}</InfoValue>
          </InfoRow>
        )}

        {settings.length > 0 && (
          <SettingsSection>
            {settings.map((setting) => (
              <InfoRow key={setting.label}>
                <InfoLabel>⚙️</InfoLabel>
                <InfoValue>
                  <SettingLabel>{setting.label}:</SettingLabel> {setting.value}
                </InfoValue>
              </InfoRow>
            ))}
          </SettingsSection>
        )}

        <ActionsBar>
          <ActionButton onClick={onGoToPlan} data-testid="popover-go-to-plan">
            📄 Plan
          </ActionButton>
          <ActionButton onClick={onGoToEvent} data-testid="popover-go-to-event">
            📅 Event
          </ActionButton>
          <ActionButton
            onClick={onFilterPlan}
            data-testid="popover-filter-plan"
          >
            🔍 Filter
          </ActionButton>
        </ActionsBar>
      </PopoverBody>
    </PopoverContainer>
  );
}

function getStatusLabel(status: string): string {
  switch (status) {
    case "SUCCESS":
      return "✅ Success";
    case "FAILED":
      return "❌ Failed";
    case "PARTIAL_SUCCESS":
      return "⚠️ Partial Success";
    case "IN_PROGRESS":
      return "🔄 In Progress";
    case "TIMEOUT":
      return "⏱️ Timeout";
    case "APPROVAL_REQUIRED":
      return "🔒 Approval Required";
    case "APPROVAL_TIMEOUT":
      return "🔒 Approval Timeout";
    case "WAITING_FOR_APPROVAL":
      return "🔒 Waiting for Approval";
    default:
      return status;
  }
}

function formatTime(isoString: string): string {
  try {
    return new Date(isoString).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
}

const PopoverContainer = styled.div<{ $accentColor?: string }>`
  background: ${theme.color.background.ui01};
  border: 1px solid
    ${({ $accentColor }) => $accentColor ?? theme.color.border.border01};
  border-top: 3px solid
    ${({ $accentColor }) => $accentColor ?? theme.color.interactive.primary};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  min-width: 220px;
  max-width: 290px;
  overflow: hidden;
  z-index: 1000;
`;

const PopoverHeader = styled.div`
  padding: 12px 14px 8px;
`;

const ActionName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${theme.color.text.text01};
  line-height: 1.3;
`;

const PopoverBody = styled.div`
  padding: 0 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  flex-shrink: 0;
  line-height: 1.5;
`;

const InfoValue = styled.span`
  font-size: 12px;
  color: ${theme.color.text.text02};
  line-height: 1.5;
`;

const SettingsSection = styled.div`
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px dashed ${theme.color.border.border01};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SettingLabel = styled.span`
  font-weight: 600;
  color: ${theme.color.text.text01};
`;

const ActionsBar = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${theme.color.border.border01};
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 8px;
  border: 1px solid ${theme.color.border.border01};
  border-radius: 6px;
  background: ${theme.color.background.ui02};
  color: ${theme.color.text.text01};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${theme.color.background.ui03};
    border-color: ${theme.color.border.border02};
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${({ $status }) => {
    switch ($status) {
      case "SUCCESS":
        return "#e8f5e9";
      case "FAILED":
        return "#ffebee";
      case "PARTIAL_SUCCESS":
        return "#fff3e0";
      case "IN_PROGRESS":
        return "#e3f2fd";
      default:
        return "#f5f5f5";
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case "SUCCESS":
        return "#2e7d32";
      case "FAILED":
        return "#c62828";
      case "PARTIAL_SUCCESS":
        return "#e65100";
      case "IN_PROGRESS":
        return "#1565c0";
      default:
        return "#616161";
    }
  }};
`;
